/**
 * dcent-pdp-gallery.js — 커머스 히어로 갤러리 컨트롤러 (DESIGN_GUIDE §16).
 *
 * 다루는 것은 갤러리 상태(활성 이미지) 하나뿐이다. 담기·옵션 상태는 dcent-pdp-buy.js 가
 * 전담하므로 여기서 카트를 건드리지 않는다.
 *
 * PC   : 화살표·썸네일로 활성 슬라이드를 바꾸고, 활성 한 장만 존 안에 보인다(순환 이동).
 * 모바일: 같은 슬라이드가 scroll-snap 캐러셀로 늘어서고 스와이프가 활성 상태를 되돌린다.
 *         썸네일 탭은 스르륵 지나가지 않게 즉시 점프(behavior: auto)한다.
 *
 * data 속성 이름은 W0 계약이라 바꾸지 않는다.
 *
 * 바깥과의 접점은 하나다 — `dcent:pdp:state`(dcent-pdp-buy.js 가 쏜다)를 받아 그 조합에 걸린
 * 이미지로 옮겨간다. 짝은 슬라이드의 `data-media-id` ↔ 이벤트의 `option.mediaId` 다.
 */

const RAIL_MARGIN = 8; // 레일 자동 스크롤 시 가장자리에 남기는 여백

function prefersReducedMotion() {
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

function bindGallery(root) {
  const slidesEl = root.querySelector("[data-dcent-gallery-slides]");
  const slides = [...root.querySelectorAll("[data-dcent-gallery-slide]")];
  const thumbs = [...root.querySelectorAll("[data-dcent-gallery-thumb]")];
  const rail = root.querySelector("[data-dcent-gallery-rail]");
  const railScroll = rail ? rail.parentElement : null;
  if (!slides.length) return;

  // 1번 슬롯이 씬이면 그 슬롯일 때만 배경 씬을 드러낸다(블렌드 상태)
  const hasScene = slides[0].classList.contains("is-scene");
  // 서버가 URL ?variant= 진입에서 해당 이미지 슬라이드를 is-active 로 찍어 보낸다(2026-08-19).
  // 마크업이 곧 초기 상태다 — 0 으로 고정하면 첫 paint 가 그 표시를 슬롯 0 으로 되돌려 버린다.
  let active = Math.max(
    0,
    slides.findIndex((el) => el.classList.contains("is-active")),
  );

  const paint = () => {
    slides.forEach((el, i) => el.classList.toggle("is-active", i === active));
    thumbs.forEach((el, i) =>
      el.setAttribute("aria-current", i === active ? "true" : "false"),
    );
    root.dataset.blended = hasScene && active === 0 ? "true" : "false";
  };

  // 모바일 캐러셀만 실제로 가로 스크롤을 갖는다(PC 는 슬라이드를 겹쳐 쌓아 스크롤이 없다)
  const isCarousel = () =>
    !!slidesEl && slidesEl.scrollWidth > slidesEl.clientWidth + 1;

  const syncCarousel = () => {
    if (!isCarousel()) return;
    const target = slidesEl.clientWidth * active;
    // 썸네일 탭·화살표로 건너뛸 때 중간 슬라이드가 스르륵 지나가지 않게 즉시 점프한다.
    // 목표 위치가 정확히 clientWidth 배수라 뒤따르는 scroll 이벤트는 같은 index 를 내고 멈춘다.
    if (Math.abs(slidesEl.scrollLeft - target) > 4) {
      slidesEl.scrollTo({ left: target, behavior: "auto" });
    }
  };

  // 활성 썸네일이 레일 가시영역 밖이면 레일만 스크롤해 보이게 한다(PC 세로 / 모바일 가로)
  const revealThumb = () => {
    const btn = thumbs[active];
    if (!btn || !railScroll) return;
    const b = btn.getBoundingClientRect();
    const c = railScroll.getBoundingClientRect();
    let top = railScroll.scrollTop;
    let left = railScroll.scrollLeft;
    if (b.top < c.top) top += b.top - c.top - RAIL_MARGIN;
    else if (b.bottom > c.bottom) top += b.bottom - c.bottom + RAIL_MARGIN;
    if (b.left < c.left) left += b.left - c.left - RAIL_MARGIN;
    else if (b.right > c.right) left += b.right - c.right + RAIL_MARGIN;
    if (top === railScroll.scrollTop && left === railScroll.scrollLeft) return;
    railScroll.scrollTo({
      top,
      left,
      behavior: prefersReducedMotion() ? "auto" : "smooth",
    });
  };

  /** @param {number} next 목표 index (범위를 벗어나면 순환) */
  /** @param {boolean} [fromScroll] 스와이프가 부른 경우 캐러셀을 되밀지 않는다 */
  const show = (next, fromScroll) => {
    active = ((next % slides.length) + slides.length) % slides.length;
    paint();
    if (!fromScroll) syncCarousel();
    revealThumb();
  };

  // ── variant 선택 따라가기 (2026-08-13) ──────────────────────────────────────
  // 구매 컬럼에서 옵션을 바꾸면 그 조합에 걸린 이미지로 갤러리가 옮겨간다. 접점은 media id
  // 하나뿐이라 갤러리는 variant 도 상품도 알 필요가 없다 — 슬라이드에 붙은 data-media-id 와
  // 이벤트가 실어 온 id 를 맞춰 보기만 한다.
  //
  // 아무 일도 안 일어나는 경우가 정상 경로에 둘 있다:
  //   · 어드민에서 variant 에 이미지를 안 건 상품 → 이벤트의 id 가 비어 있다
  //   · URL override(gallery_urls)로 그린 갤러리 → 슬라이드에 media id 가 없다
  const slideByMedia = new Map();
  slides.forEach((el, i) => {
    const id = el.getAttribute("data-media-id");
    if (id && !slideByMedia.has(id)) slideByMedia.set(id, i);
  });
  if (slideByMedia.size) {
    document.addEventListener("dcent:pdp:state", (e) => {
      const detail = e.detail || {};
      // 이 이벤트는 옵션 전용이 아니다. drop-07 에서 수량 축이 생기면서 `+`/`−` 를 눌러도
      // 같은 detail 이 날아온다 — `option.mediaId` 까지 그대로 실려서, 받는 쪽에서 내용만 보면
      // "옵션이 바뀌었다" 와 구분되지 않는다. 그래서 발신부가 이유를 실어 준다.
      //
      // 따라가야 하는 이유는 `option`(카드 전환·애드온 토글) 하나뿐이다.
      //   init — 진입 첫 방출. 첫 화면은 씬(디자인 첫인상)이어야 하므로 움직이지 않는다.
      //   qty  — 수량만 바뀐 것. 슬라이드를 건드리면 첫인상 씬이 제품 컷으로 갈아치워진다.
      // reason 이 없는 옛 방출은 `initial` 로 갈라 종전 거동을 지킨다.
      const reason = detail.reason || (detail.initial ? "init" : "option");
      if (reason !== "option") return;
      const opt = detail.option;
      if (!opt) return;
      const mediaId =
        detail.addon && opt.addonMediaId ? opt.addonMediaId : opt.mediaId;
      if (!mediaId) return;
      const idx = slideByMedia.get(String(mediaId));
      if (idx === undefined || idx === active) return;
      show(idx);
    });
  }

  thumbs.forEach((btn, i) => btn.addEventListener("click", () => show(i)));
  root
    .querySelector("[data-dcent-gallery-prev]")
    ?.addEventListener("click", () => show(active - 1));
  root
    .querySelector("[data-dcent-gallery-next]")
    ?.addEventListener("click", () => show(active + 1));

  if (slidesEl) {
    slidesEl.addEventListener(
      "scroll",
      () => {
        if (!isCarousel()) return;
        const idx = Math.round(slidesEl.scrollLeft / slidesEl.clientWidth);
        if (idx !== active && idx >= 0 && idx < slides.length) show(idx, true);
      },
      { passive: true },
    );
  }

  // 폭이 바뀌면(회전·PC↔모바일 전환) 캐러셀 위치가 어긋난다 — 활성 슬라이드로 다시 맞춘다
  let resizeTimer = 0;
  window.addEventListener("resize", () => {
    window.clearTimeout(resizeTimer);
    resizeTimer = window.setTimeout(() => {
      syncCarousel();
      revealThumb();
    }, 120);
  });

  paint();
  // ?variant= 진입으로 0 이 아닌 슬라이드에서 시작하면 모바일 캐러셀·레일도 그 위치로 맞춘다.
  if (active > 0) {
    syncCarousel();
    revealThumb();
  }
}

function init() {
  document.querySelectorAll("[data-dcent-gallery]").forEach(bindGallery);
}

if (document.readyState === "loading") {
  document.addEventListener("DOMContentLoaded", init);
} else {
  init();
}
